import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { RoleResponseModel } from "../../_core/_model.ts";
import { GetFilters } from "../../_core/_request.ts";
import { Separators } from "../../Constants.ts";
import { useAppContext } from "../../context/AppContext.tsx";
import RoleCondition from "./RoleCondition.tsx";

interface RoleDetailsProps {
    roleId: string;
}

export function RoleDetailsOnTitle({ roleId }: RoleDetailsProps) {
    const [roleDetails, setRoleDetails] = useState<RoleResponseModel | null | undefined>(undefined);
    const { roles, dimensions } = useAppContext();

    const role = roles.find((x) => x.roleID === parseInt(roleId));

    const roleDimension = role
        ? dimensions.find(d => d.dimensionCode === role.dataMapCode || d.dimensionCode === (role as any).dimensionCode)
        : null;

    useEffect(() => {
        let isMounted = true;

        const fetchRoleDetails = async () => {
            if (role) {
                try {
                    const response = await GetFilters({ roleID: parseInt(roleId) });
                    if (isMounted) {
                        if (response.data && response.data.length > 0) {
                            setRoleDetails({
                                ...role,
                                filterConditions: response.data[0].filterConditions
                            });
                        } else {
                            setRoleDetails(null);
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch role filters:", error);
                    if (isMounted) setRoleDetails(null);
                }
            } else {
                if (isMounted) setRoleDetails(null);
            }
        };

        fetchRoleDetails();

        return () => {
            isMounted = false;
        };
    }, [roleId, roles, role]);

    return (
        <div
            className="pe-1"
            style={{
                width: "100%",
                minWidth: "700px",
                maxWidth: "700px",
                maxHeight: "450px",
                overflowY: "auto",
                overflowX: "hidden",     // Truncation handles long names now, no horizontal scroll needed
                scrollbarWidth: "thin"
            }}
        >
            <AnimatePresence mode="wait">
                {typeof roleDetails === 'undefined' ? (
                    // 1. SKELETON LOADING STATE (Updated to match flex-nowrap)
                    <motion.div
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="placeholder-glow m-0"
                    >
                        <div className="d-flex justify-content-between align-items-center mb-2 pe-1">
                            <div className="opacity-75 small fw-semibold text-uppercase" style={{ fontSize: '0.70rem', width: '30%' }}>
                                <span className="placeholder col-12 rounded"></span>
                            </div>
                            <div className="placeholder col-3 rounded-pill" style={{ height: '26px', minWidth: '90px' }}></div>
                        </div>

                        <div className="mb-4 pe-1">
                            <div className="fs-6 lh-sm d-flex flex-column gap-2 w-100">
                                <span className="placeholder col-12 rounded"></span>
                                <span className="placeholder col-9 rounded"></span>
                            </div>
                        </div>

                        <div className="w-100 pe-1">
                            <div className="opacity-75 small fw-semibold text-uppercase mb-2" style={{ fontSize: '0.70rem', letterSpacing: '0.5px' }}>
                                <span className="placeholder col-5 rounded"></span>
                            </div>
                            <div className="border border-opacity-25 p-2 rounded-3 shadow-sm bg-light bg-opacity-50">
                                {/* Updated Skeleton to mirror the new RoleCondition layout */}
                                <div className="d-flex flex-nowrap align-items-center gap-2 border rounded-3 opacity-50 bg-white px-2 py-1 my-1">
                                    <span className="placeholder col-5 rounded"></span>
                                    <span className="placeholder badge rounded" style={{ width: '24px' }}></span>
                                    <span className="placeholder col-3 rounded"></span>
                                    <span className="placeholder badge rounded ms-auto" style={{ width: '30px' }}></span>
                                </div>
                                <div className="d-flex flex-nowrap align-items-center gap-2 border rounded-3 opacity-50 bg-white px-2 py-1 my-1">
                                    <span className="placeholder col-4 rounded"></span>
                                    <span className="placeholder badge rounded" style={{ width: '24px' }}></span>
                                    <span className="placeholder col-4 rounded"></span>
                                    <span className="placeholder badge rounded ms-auto" style={{ width: '30px' }}></span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : roleDetails === null ? (
                    // 2. NOT FOUND STATE
                    <motion.div
                        key="not-found"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.2 }}
                        className="text-muted text-center py-4 d-flex flex-column align-items-center"
                    >
                        <span className="fs-5 mb-1">🔍</span>
                        <span style={{ fontSize: '0.9rem' }}>Details not found</span>
                    </motion.div>
                ) : (
                    // 3. LOADED STATE
                    <motion.div
                        key="loaded"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="m-0 pb-1"
                    >
                        {/* Header Row */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: '0.70rem', letterSpacing: '0.6px' }}>
                                Description
                            </div>

                            {roleDimension && (
                                <div
                                    className="badge rounded-pill d-flex align-items-center gap-2 shadow-sm flex-shrink-0"
                                    style={{
                                        backgroundColor: roleDimension.bg,
                                        color: roleDimension.color,
                                        border: `1px solid ${roleDimension.color}30`,
                                        padding: '0.35rem 0.75rem',
                                    }}
                                    title={`Belongs to ${roleDimension.dimensionName} dimension`}
                                >
                                    <span
                                        style={{
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            backgroundColor: roleDimension.color,
                                            boxShadow: `0 0 4px ${roleDimension.color}`
                                        }}
                                    />
                                    <span className="fw-bold" style={{ fontSize: '0.75rem', letterSpacing: '0.3px' }}>
                                        {roleDimension.dimensionName}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Description Text */}
                        <div className="mb-4 pe-1">
                            <div className="fs-6 lh-base text-dark" style={{ fontWeight: '500', wordBreak: 'break-word' }}>
                                {roleDetails.description || <span className="text-muted fst-italic">No description provided.</span>}
                            </div>
                        </div>

                        {/* Filter Conditions */}
                        <div className="w-100 pe-1">
                            <div className="text-muted small fw-bold text-uppercase mb-2" style={{ fontSize: '0.70rem', letterSpacing: '0.6px' }}>
                                Filter Conditions
                            </div>
                            <div className="border border-opacity-25 p-2 rounded-3 shadow-sm bg-light bg-opacity-25 w-100">
                                {roleDetails.filterConditions ? (
                                    <div className="d-flex flex-column gap-1 w-100">
                                        {roleDetails.filterConditions.split(Separators.row).map((condition, idx) => (
                                            condition.length > 0 && (
                                                <RoleCondition condition={condition} key={idx} />
                                            )
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-muted small text-center py-3 fst-italic">
                                        No conditions applied
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default RoleDetailsOnTitle;